import * as React from "react";
import { usePathname, useNavigate as useRouter, useSearch as useSearchParams } from "../../router";
import { cn } from "../../utils";
import { Card } from "../../card";
import { formatDate } from "../../format";
import { sectionColor } from "../../tokens";
import {
  PRIORITY_LABEL,
  PRIORITY_TONE,
  type ScopedTask,
  type ViewScope,
} from "../../types/projects";

interface TaskCardProps {
  task: ScopedTask;
  scope: ViewScope;
  draggable?: boolean;
  pending?: boolean;
  className?: string;
}

/**
 * Compact task card used in kanban columns, the planner grid, and any list
 * view that wants to deep-link into the task peek. Clicking the card
 * navigates to the same page with `?task=<id>` appended, which
 * `TaskPeekHost` (mounted in the root layout) picks up.
 *
 * Layout follows the shadcn card hierarchy: the **title is the hero**
 * (foreground, medium weight, up to two lines), and everything else is a
 * single quiet metadata footer — project and section render as a hued dot
 * plus muted text (never a loud all-caps pill, which would out-shout the
 * title), with the due date pushed to the trailing edge.
 */
export function TaskCard({
  task,
  scope,
  draggable,
  pending,
  className,
}: TaskCardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();

  function openDrawer(e: React.MouseEvent) {
    e.stopPropagation();
    const next = new URLSearchParams(search.toString());
    next.set("task", String(task.id));
    router.replace(`${pathname}?${next.toString()}`);
  }

  const due = task.due_date ? formatDate(task.due_date) : null;
  // Only flag priority that warrants attention; low/medium stay silent so the
  // dot reads as a signal, not chrome.
  const flaggedPriority =
    task.priority === "high" || task.priority === "urgent";
  const showFooter = scope.kind !== "section" || Boolean(due);

  return (
    // Card (the Elevated offset-1 surface) with the kanban-density overrides;
    // hover stays the quiet accent wash rather than Card's `interactive`
    // secondary step so cards read as draggable tiles, not link rows.
    <Card
      onClick={openDrawer}
      className={cn(
        "group gap-2 p-3 type-body transition-colors hover:bg-accent/40",
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
        pending && "opacity-60",
        className,
      )}
    >
        <div className="flex min-w-0 items-start gap-2">
          <span
            aria-hidden="true"
            title={PRIORITY_LABEL[task.priority]}
            className={cn(
              "mt-1 size-2 shrink-0 rounded-full",
              flaggedPriority
                ? PRIORITY_TONE[task.priority]
                : "bg-muted-foreground/25",
            )}
          />
          <span className="line-clamp-2 min-w-0 flex-1 font-medium text-foreground">
            {task.title}
          </span>
        </div>

        {showFooter ? (
          <div className="flex min-w-0 items-center gap-1.5 pl-4 type-subtext text-muted-foreground">
            {/*
             * Exactly one text label plus color dots — the board's columns are
             * narrow, so two competing labels both collapse to a single letter.
             * Workspace scope leads with the project name (what disambiguates a
             * mixed board) and demotes the section to a tooltipped dot; deeper
             * scopes already know the project, so the section gets the label.
             */}
            {scope.kind === "workspace" ? (
              <>
                <span
                  aria-hidden="true"
                  className="size-1.5 shrink-0 rounded-full"
                  style={{
                    backgroundColor:
                      task.project_color ?? "var(--muted-foreground)",
                  }}
                />
                <span className="min-w-0 truncate">{task.project_name}</span>
                <span
                  aria-hidden="true"
                  title={task.section_name}
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: sectionColor(task.section_slug) }}
                />
              </>
            ) : scope.kind === "project" ? (
              <>
                <span
                  aria-hidden="true"
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: sectionColor(task.section_slug) }}
                />
                <span className="min-w-0 truncate">{task.section_name}</span>
              </>
            ) : null}
            {due ? (
              <span className="ml-auto shrink-0 pl-1 tabular-nums">{due}</span>
            ) : null}
          </div>
        ) : null}
    </Card>
  );
}
